import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Chip, Tooltip } from '@mui/material';
import {
  HourglassEmpty as ProcessingIcon,
  VideocamOff as UnavailableIcon,
} from '@mui/icons-material';

import { isVimeoVideoDeleted } from '../../utils/vimeo';
import IconWrapper from './styles';
import texts from './texts.json';

const videoIssues = {
  processing: {
    label: texts.processing,
    tooltip: texts.processingTooltip,
    color: 'warning',
    Icon: ProcessingIcon,
  },
  unavailable: {
    label: texts.unavailable,
    tooltip: texts.unavailableTooltip,
    color: 'error',
    Icon: UnavailableIcon,
  },
};

/**
 * Flags an exercise whose video clients can't play yet (still processing) or anymore (deleted on Vimeo).
 * Renders nothing when the video is fine or when the exercise has no video at all, since many exercises
 * are intentionally video-less.
 */
const ExerciseVideoIssueChip = ({
  videoUrl,
  isVideoProcessing,
  iconOnly,
  className,
}) => {
  const [isVideoDeleted, setIsVideoDeleted] = useState(false);

  useEffect(() => {
    let isCurrent = true;
    setIsVideoDeleted(false);

    // Checked even while processing: an exercise getting a new video still serves the old one to clients
    // until processing finishes, and that one may be gone.
    if (videoUrl) {
      isVimeoVideoDeleted(videoUrl).then((isDeleted) => {
        if (isCurrent) {
          setIsVideoDeleted(isDeleted);
        }
      });
    }

    return () => {
      isCurrent = false;
    };
  }, [videoUrl]);

  let issue = null;
  if (isVideoDeleted) {
    issue = videoIssues.unavailable;
  } else if (isVideoProcessing) {
    issue = videoIssues.processing;
  }

  if (!issue) {
    return null;
  }

  const {
    label,
    tooltip,
    color,
    Icon,
  } = issue;

  if (iconOnly) {
    // The tooltip text becomes the span's accessible name, so screen readers still hear the issue.
    return (
      <Tooltip title={`${label}: ${tooltip}`} arrow>
        <IconWrapper role="img" className={className}>
          <Icon color={color} fontSize="small" />
        </IconWrapper>
      </Tooltip>
    );
  }

  // describeChild keeps the chip's label as its accessible name and adds the tooltip as its description.
  return (
    <Tooltip title={tooltip} arrow describeChild>
      <Chip
        className={className}
        size="small"
        variant="outlined"
        color={color}
        icon={<Icon />}
        label={label}
      />
    </Tooltip>
  );
};

ExerciseVideoIssueChip.propTypes = {
  videoUrl: PropTypes.string,
  isVideoProcessing: PropTypes.bool,
  // Icon without the label, for tight spaces like the exercise picker's name cell.
  iconOnly: PropTypes.bool,
  className: PropTypes.string,
};

ExerciseVideoIssueChip.defaultProps = {
  videoUrl: '',
  isVideoProcessing: false,
  iconOnly: false,
  className: '',
};

export default ExerciseVideoIssueChip;
